import React, { forwardRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useDsTokens } from '../theme/useDsTokens';
import { DsResolvedTokens } from '../theme/tokens';
import { cn } from '../utils/cn';
import { DsPrimitiveProps, SurfacePadding, SurfaceVariant } from './types';

export interface SurfaceProps extends DsPrimitiveProps {
  variant?: SurfaceVariant;
  padding?: SurfacePadding;
  elevated?: boolean;
  interactive?: boolean;
  disabled?: boolean;
  as?: 'div' | 'section' | 'article';
  children?: React.ReactNode;
}

interface StyleProps {
  tokens: DsResolvedTokens;
  fill: string;
  variant: SurfaceVariant;
  paddingPx: number;
  elevated: boolean;
  interactive: boolean;
  disabled: boolean;
}

function resolveFill(variant: SurfaceVariant, tokens: DsResolvedTokens): string {
  switch (variant) {
    case 'app':
      return tokens.surfaceApp;
    case 'raised':
      return tokens.surfaceRaised;
    case 'sunken':
      return tokens.surfaceSunken;
    case 'input':
      return tokens.surfaceInput;
    case 'overlay':
      return tokens.surfaceOverlay;
    default:
      return tokens.surfaceRaised;
  }
}

function resolvePadding(padding: SurfacePadding, tokens: DsResolvedTokens): number {
  switch (padding) {
    case 'none':
      return 0;
    case 'sm':
      return tokens.space2;
    case 'lg':
      return tokens.space4;
    case 'md':
    default:
      return tokens.space3;
  }
}

const useStyles = makeStyles({
  root: (props: StyleProps) => ({
    backgroundColor: props.fill,
    padding: props.paddingPx,
    borderRadius: props.variant === 'raised' ? props.tokens.radiusLg : undefined,
    border:
      props.variant === 'raised' && props.elevated
        ? `${props.tokens.borderWidthDefault}px solid ${props.tokens.borderDefault}`
        : undefined,
    boxShadow: props.variant === 'raised' && props.elevated ? props.tokens.elevation1 : undefined,
    opacity: props.disabled ? props.tokens.opacityDisabled : 1,
    pointerEvents: props.variant === 'overlay' ? 'auto' : undefined,
    transition: `background-color ${props.tokens.motionDurationMicro}ms ${props.tokens.motionEasingStandard}`,
    ...(props.interactive && props.variant === 'raised'
      ? {
          '&:hover': {
            backgroundColor: props.tokens.interactionHover,
          },
        }
      : {}),
    ...(props.variant === 'raised'
      ? {
          '&:focus-within': {
            outline: `${props.tokens.borderWidthStrong}px solid ${props.tokens.borderFocus}`,
            outlineOffset: 2,
          },
        }
      : {}),
  }),
});

export const Surface = forwardRef<HTMLElement, SurfaceProps>(function Surface(
  {
    id,
    className,
    variant = 'raised',
    padding = 'md',
    elevated = false,
    interactive = false,
    disabled = false,
    as: Component = 'div',
    children,
    'data-testid': testId,
  },
  ref
) {
  const tokens = useDsTokens();
  const fill = resolveFill(variant, tokens);
  const paddingPx = resolvePadding(padding, tokens);

  const classes = useStyles({
    tokens,
    fill,
    variant,
    paddingPx,
    elevated,
    interactive,
    disabled,
  });

  const overlayProps =
    variant === 'overlay'
      ? { 'aria-hidden': true as const }
      : {};

  return React.createElement(
    Component,
    {
      ref,
      id,
      className: cn(classes.root, className),
      'data-testid': testId,
      ...overlayProps,
    },
    children
  );
});

Surface.displayName = 'Surface';
